import { useState, useEffect, useRef, useMemo } from 'react';
import { ContentCard } from '../../ContentCard/ContentCard';
import { chevron_left, close, green_checkmark, Icon } from '../../Icon/Icon';
import { Modal } from '../../Modal/Modal';
import { SimpleSearchFormLoop54 } from '../../SimpleSearchFormLoop54/SimpleSearchFormLoop54';
import './GenerateSpecialPriceModal.css';
import { ApiSearchLoop54, ApiProduct, Loop54SearchRequestBody, ApiSpecialsProduct } from '../../../types';
import { Button } from '../../Form/Button';
import { LOOP54_DEFAULT_PAGE_SIZE } from '../../Pagination/PaginationLoop54';
import { useConfig } from '../../../config';
import { Res, transformLoopResponseToApiProducts, useApi, useCacheInvalidator } from '../../../state/api';
import { MetaContainer } from '../../../state/meta';
import { generateUUID } from '../../utils';
import { ProductBox } from '../../ProductListing/ProductBox/ProductBox';
import { HorizontalProductCardSpecialPrice } from '../../ProductListing/HorizontalProductCardSpecialPrice/HorizontalProductCardSpecialPrice';
import { Carousel } from 'react-responsive-carousel';
import { ProductContainer } from '../../../state/product';
import { GenerateSpecialPriceProductListing } from '../../ProductListing/GenerateSpecialPriceProductListing/GenerateSpecialPriceProductListing';
import axios from 'axios';
import React from 'react';
import { useTranslation } from '../../../i18n/i18n';
import { PopularCategories } from '../../PopularCategories/PopularCategories';
import { useSession } from '../../../state/session';
import SanitizedHtml from '../../SanitizedHtml/SanitizedHtml';

interface GenerateSpecialPriceModalProps {
	open: boolean;
	setOpen: (open: boolean) => void;
	onClose: () => void;
	expiredProductId?: number;
}

export const GenerateSpecialPriceModal = ({
	open,
	setOpen,
	onClose,
	expiredProductId,
}: GenerateSpecialPriceModalProps): JSX.Element => {
	const [query, setQuery] = useState<string>('');
	const [initialInputValue, setInitialInpuValue] = useState<string | null>();
	const [searchResults, setSearchResults] = useState<(ApiProduct | ApiSpecialsProduct)[]>([]);
	const [page, setPage] = useState<number>(1);
	const [totalCount, setTotalCount] = useState<number>(0);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [generatingPrice, setGeneratingPrice] = useState(false);
	const [specialPriceSelected, setSpecialPriceSelected] = useState(false);
	const [selectedProduct, setSelectedProduct] = useState<ApiProduct>();
	const [newPrice, setNewPrice] = useState<number>();
	const [generateRequestCompleted, setGenerateRequestCompleted] = useState(false);
	const [loaderAnimationCompleted, setLoaderAnimationCompleted] = useState(false);
	const [runLoaderAnimation, setRunLoaderAnimation] = useState(false);
	const config = useConfig();
	const { searchProfileId, randomizeSearchProfileId } = MetaContainer.useContainer();
	const { specialPricesIndex, getSpecialPriceSuggestion } = ProductContainer.useContainer();
	const { data: specialPriceSuggestions } = useApi(Res.specialPricesSuggestions, {});
	const mutate = useCacheInvalidator();
	const t = useTranslation().specialPrices;

	useEffect(() => {
		if (selectedProduct && specialPriceSuggestions) {
			const newSpecialPrice = getSpecialPriceSuggestion(Number(selectedProduct.id));
			setNewPrice(newSpecialPrice?.price);
		}
	}, [specialPriceSuggestions, selectedProduct]);

	useEffect(() => {
		if (generateRequestCompleted && !loaderAnimationCompleted) {
			mutate(Res.specialPricesSuggestions, {});
			setGenerateRequestCompleted(false);
		}
	}, [generateRequestCompleted, loaderAnimationCompleted, mutate]);

	const closeModal = () => {
		setOpen(false);
		setSearchResults([]);
		setPage(1);
		setQuery('');
		setInitialInpuValue(null);
		setTotalCount(0);
		setGeneratingPrice(false);
		setSpecialPriceSelected(false);
		onClose();
	};

	const goBack = () => {
		setSearchResults([]);
		setPage(1);
		setQuery('');
		setInitialInpuValue(null);
		setTotalCount(0);
		setGeneratingPrice(false);
		setSpecialPriceSelected(false);
		setLoaderAnimationCompleted(false);
	};

	const handleSearch = (newQuery: string) => {
		setQuery(newQuery);
		setInitialInpuValue(newQuery);
		setPage(1);
	};

	const loadMore = () => {
		setPage((prevPage) => prevPage + 1);
	};

	const fetchSearchedProducts = async () => {
		setIsLoading(true);
		try {
			const payload: Loop54SearchRequestBody = {
				query,
				resultsOptions: { take: LOOP54_DEFAULT_PAGE_SIZE, skip: (page - 1) * LOOP54_DEFAULT_PAGE_SIZE },
				relatedResultsOptions: { take: 0, skip: 0 },
				spellingSuggestionsOptions: { take: 0, skip: 0 },
			};

			const headers = {
				'Api-Version': 'V3',
				'User-id': randomizeSearchProfileId ? generateUUID() : searchProfileId,
			};

			const response = await fetch(`${config.loop54Api}search`, {
				method: 'POST',
				headers,
				body: JSON.stringify(payload),
			});

			if (!response.ok) {
				throw new Error('Network error');
			}

			const data: ApiSearchLoop54 = await response.json();
			const transformedProducts: ApiProduct[] = transformLoopResponseToApiProducts(data.results.items as any);
			const apiProductReplacedWithSpecialProducts: (ApiProduct | ApiSpecialsProduct)[] = transformedProducts.map(
				(product) => (specialPricesIndex[product.id] ? specialPricesIndex[product.id] : product)
			);

			if (page === 1) {
				setSearchResults(apiProductReplacedWithSpecialProducts);
			} else {
				setSearchResults((prev) => [...prev, ...apiProductReplacedWithSpecialProducts]);
			}

			setTotalCount(data.results.count);
		} catch (error) {
			console.error('Error fetching search results:', error);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		if (!query) {
			setSearchResults([]);
			setTotalCount(0);
			return;
		}
		fetchSearchedProducts();
	}, [query, page]);

	const handleSpecialPriceProductClick = (action: 'accept' | 'generate', product: ApiProduct) => {
		if (action === 'generate') generateSpecialPrice(product);
		if (action === 'accept') acceptSpecialPrice(product);
	};

	const acceptSpecialPrice = async (product: ApiProduct) => {
		try {
			const suggestionId = getSpecialPriceSuggestion(product.id)?.id;
			if (suggestionId) {
				const payload = expiredProductId ? { replacing: expiredProductId } : null;
				const res = await axios.post(
					`${config.api}me/specials/suggestions/${suggestionId}/accept.json`,
					payload,
					{
						withCredentials: true,
					}
				);
				if (res.data.success) {
					mutate(Res.specialsWithProducts, {});
					mutate(Res.specialsPriceEvents, {});
					closeModal();
				}
			}
		} catch (error) {
			console.error(error);
		}
	};

	const generateSpecialPrice = async (product: ApiProduct) => {
		setGeneratingPrice(true);
		setSpecialPriceSelected(false);
		setSelectedProduct(product);
		setRunLoaderAnimation(true);

		try {
			const payload = { productId: product.id };
			const res = await axios.post(`${config.api}me/specials/suggestions.json`, payload, {
				withCredentials: true,
			});
			if (res.data.success) {
				setGenerateRequestCompleted(true);
			}
		} catch (error) {
			console.error(error);
		}
	};

	const handleAnimationComplete = () => {
		setRunLoaderAnimation(false);
		setGeneratingPrice(false);
		setSpecialPriceSelected(true);
		setLoaderAnimationCompleted(true);
		mutate(Res.specialPricesSuggestions, {});
	};

	return (
		<Modal
			className="generate-special-price-modal"
			show={open}
			disableBodyScroll
			onDismiss={closeModal}
			toggle={() => setOpen(!open)}
		>
			<ContentCard className="generate-special-price-content">
				<div className="modal-header">
					<div className="modal-header-title">
						{specialPriceSelected ? (
							<div className="back-navigation" onClick={() => goBack()}>
								<Icon shape={chevron_left} size={24} />
								{t.back}
							</div>
						) : generatingPrice ? (
							<>{t.generatingPrice}</>
						) : (
							<>{t.addProduct}</>
						)}
					</div>
					<button className="close-btn" onClick={closeModal}>
						<Icon shape={close} size={20} />
					</button>
				</div>
				<div className={`modal-body ${specialPriceSelected ? 'special-price-selected' : ''}`}>
					{generatingPrice && selectedProduct ? (
						<GeneratingSpecialPriceView
							selectedProduct={selectedProduct}
							onAnimationComplete={handleAnimationComplete}
							run={runLoaderAnimation}
						/>
					) : specialPriceSelected && selectedProduct && newPrice ? (
						<SpecialPriceSelectedView
							selectedProduct={selectedProduct}
							newPrice={newPrice}
							handleOnClick={handleSpecialPriceProductClick}
						/>
					) : (
						<>
							<SimpleSearchFormLoop54 onSearch={handleSearch} initialInputValue={initialInputValue} />
							<ResultText count={totalCount} query={query} isLoading={isLoading} />
							{totalCount === 0 && <PopularCategories store={config.store} onClick={handleSearch} />}
							{searchResults.length > 0 && (
								<div className={`search-results-wrapper ${isLoading ? 'loading' : ''}`}>
									<GenerateSpecialPriceProductListing
										products={searchResults}
										specialPriceOnClick={handleSpecialPriceProductClick}
									/>
								</div>
							)}
							<PaginationFooter count={totalCount} page={page} loading={isLoading} onClick={loadMore} />
						</>
					)}
				</div>
			</ContentCard>
		</Modal>
	);
};

const GeneratingSpecialPriceView = ({
	selectedProduct,
	onAnimationComplete,
	run,
}: {
	selectedProduct: ApiProduct;
	onAnimationComplete: () => void;
	run: boolean;
}) => {
	const playInterval = 5000;
	const { store } = useConfig();
	const { user } = useSession();
	let carouselRef = useRef<Carousel>(null);
	let firstname = '';
	if (user) firstname = user.firstname;

	const slideContent = useMemo(
		() => ({
			kgse: [
				{
					text: '<strong>Enormt sortiment. </strong> Minimal risk att inte hitta det du söker. 😅',
				},
				{
					text: '<strong>Jättesnabbt.</strong> Beställ före 15.30 för leverans nästa vardag. ⚡',
				},
				{
					text: '<strong>Slipp Tipp-Ex.</strong> Ibland blir det fel - vi erbjuder fri returfrakt. 🎉',
				},
				{
					text: '<strong>Hållbart.</strong> ISO-certifierade med stort miljömärkt sortiment. 🌱',
				},
				{
					text: '<strong>Koll på läget.</strong> Överblick och kontroll i dina beställningar. 🚚',
				},
				{
					text: '<strong>I gott sällskap.</strong> Vi växer snabbt, 32.000 företag blev kund senaste året. 🚀',
				},
				{
					text: `<strong>Håll ut ${firstname}.</strong> Ditt pris är snart redo. ⌛`,
				},
				{
					text: '<strong>Kunderna älskar oss.</strong> 14.000 omdömen med 4,6/5 i betyg. ❤️',
				},
			],
			kgno: [
				{
					text: '<strong>Enormt sortiment.</strong> Minimal risiko for ikke å finne det du leter etter. 😅',
				},
				{
					text: '<strong>Kjempesnart.</strong> Bestill før 15.30 for levering neste virkedag. ⚡',
				},
				{
					text: '<strong>Slipp Tipp-Ex.</strong> Noen ganger blir det feil – vi tilbyr gratis returfrakt. 🎉',
				},
				{
					text: '<strong>Bærekraftig.</strong> ISO-sertifiserte med stort miljømerket sortiment. 🌱',
				},
				{
					text: '<strong>Ha kontroll på situasjonen.</strong> Oversikt og kontroll over dine bestillinger. 🚚',
				},
				{
					text: '<strong>I godt selskap.</strong> Vi vokser raskt, 32.000 bedrifter ble kunde det siste året. 🚀',
				},
				{
					text: `<strong>Hold ut ${firstname}.</strong> Prisen din er snart klar. ⌛`,
				},
				{
					text: '<strong>Kundene elsker oss.</strong> 14.000 anmeldelser med 4,6/5 i vurdering. ❤️',
				},
			],
			kgdk: [
				{
					text: '<strong>Enormt sortiment.</strong> Minimal risiko for ikke at finde det, du søger. 😅',
				},
				{
					text: '<strong>Kæmpehurtigt.</strong> Bestil før 15.30 for levering næste hverdag. ⚡',
				},
				{
					text: '<strong>Slip Tipp-Ex.</strong> Nogle gange går det galt – vi tilbyder gratis returfragt. 🎉',
				},
				{
					text: '<strong>Bæredygtig.</strong> ISO-certificerede med stort miljømærket sortiment. 🌱',
				},
				{
					text: '<strong>Få styr på situationen.</strong> Overblik og kontrol over dine bestillinger. 🚚',
				},
				{
					text: '<strong>I godt selskab.</strong> Vi vokser hurtigt, 32.000 virksomheder blev kunde det sidste år. 🚀',
				},
				{
					text: `<strong>Hold ud ${firstname}.</strong> Din pris er snart klar. ⌛`,
				},
				{
					text: '<strong>Kunderne elsker os.</strong> 14.000 anmeldelser med 4,6/5 i bedømmelse. ❤️',
				},
			],
			kgat: [
				{
					text: '<strong>Enormes Sortiment.</strong> Minimales Risiko, nicht das zu finden, was Sie suchen. 😅',
				},
				{
					text: '<strong>Super schnell.</strong> Bestellen Sie vor 15.30 Uhr für Lieferung am nächsten Werktag. ⚡',
				},
				{
					text: '<strong>Kein Tipp-Ex nötig.</strong> Manchmal geht etwas schief – wir bieten kostenlose Rücksendung. 🎉',
				},
				{
					text: '<strong>Nachhaltig.</strong> ISO-zertifiziert mit großem umweltzertifiziertem Sortiment. 🌱',
				},
				{
					text: '<strong>Immer im Blick.</strong> Übersicht und Kontrolle über Ihre Bestellungen. 🚚',
				},
				{
					text: '<strong>In guter Gesellschaft.</strong> Wir wachsen schnell, 32.000 Unternehmen wurden im letzten Jahr Kunde. 🚀',
				},
				{
					text: `<strong>Bleiben Sie dran ${firstname}.</strong> Ihr Preis ist gleich fertig. ⌛`,
				},
				{
					text: '<strong>Kunden lieben uns.</strong> 14.000 Bewertungen mit 4,6/5 Sternen. ❤️',
				},
			],
			kgde: [
				{
					text: '<strong>Enormes Sortiment.</strong> Minimales Risiko, nicht das zu finden, was Sie suchen. 😅',
				},
				{
					text: '<strong>Super schnell.</strong> Bestellen Sie vor 15.30 Uhr für Lieferung am nächsten Werktag. ⚡',
				},
				{
					text: '<strong>Kein Tipp-Ex nötig.</strong> Manchmal geht etwas schief – wir bieten kostenlose Rücksendung. 🎉',
				},
				{
					text: '<strong>Nachhaltig.</strong> ISO-zertifiziert mit großem umweltzertifiziertem Sortiment. 🌱',
				},
				{
					text: '<strong>Immer im Blick.</strong> Übersicht und Kontrolle über Ihre Bestellungen. 🚚',
				},
				{
					text: '<strong>In guter Gesellschaft.</strong> Wir wachsen schnell, 32.000 Unternehmen wurden im letzten Jahr Kunde. 🚀',
				},
				{
					text: `<strong>Bleiben Sie dran ${firstname}.</strong> Ihr Preis ist gleich fertig. ⌛`,
				},
				{
					text: '<strong>Kunden lieben uns.</strong> 14.000 Bewertungen mit 4,6/5 Sternen. ❤️',
				},
			],
			kggb: [
				{
					text: "<strong>Huge range.</strong> Minimal risk of not finding what you're looking for. 😅",
				},
				{
					text: '<strong>Super fast.</strong> Order before 15.30 for next working day delivery. ⚡',
				},
				{
					text: '<strong>No Tipp-Ex needed.</strong> Sometimes things go wrong – we offer free returns. 🎉',
				},
				{
					text: '<strong>Sustainable.</strong> ISO-certified with a large eco-certified range. 🌱',
				},
				{
					text: '<strong>Always in view.</strong> Overview and control of your orders. 🚚',
				},
				{
					text: "<strong>In good company.</strong> We're growing fast, 32,000 businesses became customers last year. 🚀",
				},
				{
					text: `<strong>Hang in there ${firstname}.</strong> Your price is almost ready. ⌛`,
				},
				{
					text: '<strong>Customers love us.</strong> 14,000 reviews with 4.6/5 stars. ❤️',
				},
			],
			kgnl: [
				{
					text: '<strong>Enorm assortiment.</strong> Minimale kans dat u niet vindt wat u zoekt. 😅',
				},
				{
					text: '<strong>Super snel.</strong> Bestel voor 15.30 uur voor levering de volgende werkdag. ⚡',
				},
				{
					text: '<strong>Geen Tipp-Ex nodig.</strong> Soms gaat er iets mis – wij bieden gratis retour. 🎉',
				},
				{
					text: '<strong>Duurzaam.</strong> ISO-gecertificeerd met een groot milieukeurmerk assortiment. 🌱',
				},
				{
					text: '<strong>Altijd in beeld.</strong> Overzicht en controle over uw bestellingen. 🚚',
				},
				{
					text: '<strong>In goed gezelschap.</strong> We groeien snel, 32.000 bedrijven werden vorig jaar klant. 🚀',
				},
				{
					text: `<strong>Even geduld ${firstname}.</strong> Uw prijs is bijna klaar. ⌛`,
				},
				{
					text: '<strong>Klanten zijn dol op ons.</strong> 14.000 beoordelingen met 4,6/5 sterren. ❤️',
				},
			],
		}),
		[firstname]
	);

	const slides = useMemo(() => slideContent[store], [store, slideContent]);
	const numOfSlides = 3;
	const [randomizedSlides, setRandomizedSlides] = useState(() =>
		[...slides].sort(() => Math.random() - 0.5).slice(0, numOfSlides)
	);

	const handleAnimationComplete = () => {
		onAnimationComplete();
		setRandomizedSlides([...slides].sort(() => Math.random() - 0.5).slice(0, numOfSlides));
	};

	const MemoizedCarousel = React.memo(
		({ slides, playInterval }: { slides: { text: string }[]; playInterval: number }) => {
			useEffect(() => {
				const intervalId = setInterval(() => {
					if (carouselRef.current) {
						const currentSlide = carouselRef.current.state.selectedItem;
						const nextSlide = (currentSlide + 1) % slides.length;
						carouselRef.current.moveTo(nextSlide);
					}
				}, playInterval);

				return () => clearInterval(intervalId);
			}, [playInterval, slides.length]);

			return (
				<Carousel
					showThumbs={false}
					swipeable={false}
					showArrows={false}
					autoPlay={false}
					showStatus={false}
					infiniteLoop={true}
					ref={carouselRef}
					key={'carousel' + slides.length}
				>
					{slides.map((s, i) => (
						<div key={'carousel-slide' + i} className="carousel-slide">
							<ContentCard className="selected-product-message">
								<SanitizedHtml as="p" html={s.text} />
							</ContentCard>
						</div>
					))}
				</Carousel>
			);
		}
	);

	return (
		<div className="generating-special-price-view">
			<LoaderAnimation onComplete={handleAnimationComplete} run={run} />
			<div className="product-carousel-wrapper">
				<ProductBox product={selectedProduct} className="selected-product" disabled openInNewTab />
				<div className="carousel-wrapper">
					<MemoizedCarousel slides={randomizedSlides} playInterval={playInterval} />
				</div>
			</div>
		</div>
	);
};

const LoaderAnimation = React.memo(({ onComplete, run }: { onComplete: () => void; run: boolean }) => {
	const [progress, setProgress] = useState(0);
	const startTimeRef = useRef<number | null>(null);
	const durationRef = useRef<number | null>(null);
	const rafIdRef = useRef<number | null>(null);

	useEffect(() => {
		if (!run) return;

		if (startTimeRef.current === null) {
			startTimeRef.current = performance.now();
			durationRef.current = (Math.floor(Math.random() * (60 - 30 + 1)) + 30) * 1000; // bewteen 30-60 seconds
		}

		const updateProgress = () => {
			const elapsed = performance.now() - startTimeRef.current!;
			const newProgress = Math.min((elapsed / durationRef.current!) * 100, 100);
			setProgress(newProgress);

			if (newProgress < 100) {
				rafIdRef.current = requestAnimationFrame(updateProgress);
			} else {
				onComplete();
			}
		};

		rafIdRef.current = requestAnimationFrame(updateProgress);

		return () => {
			if (rafIdRef.current) {
				cancelAnimationFrame(rafIdRef.current);
			}
		};
	}, [run, onComplete]);

	return (
		<div className="loader-bar">
			<div className="loader-fill" style={{ width: `${Math.floor(progress)}%` }}>
				<span className="loader-text">{`${Math.floor(progress)}%`}</span>
			</div>
		</div>
	);
});

const SpecialPriceSelectedView = ({
	selectedProduct,
	newPrice,
	handleOnClick,
}: {
	selectedProduct: ApiProduct;
	newPrice: number;
	handleOnClick: (action: 'accept', product: ApiProduct) => void;
}) => {
	const t = useTranslation().specialPrices;
	const lockable = newPrice !== -1;
	const noPriceDiff = newPrice === selectedProduct.price;

	const today = new Date();
	const sixMonthsFromToday = new Date(today);
	sixMonthsFromToday.setMonth(sixMonthsFromToday.getMonth() + 6);
	const formattedDate = sixMonthsFromToday.toISOString().split('T')[0];

	return (
		<div className="select-price-view">
			<h3>{`${noPriceDiff ? t.alreadyReduced : lockable ? t.congratulationsToPrice : t.minPriceAlreadyReached}`}</h3>
			<p>{`${noPriceDiff ? t.alredyMinPrice : lockable ? t.acceptPrice : t.goBackAndChooseOther}`}</p>
			<div className="product-wrapper">
				<HorizontalProductCardSpecialPrice
					product={selectedProduct}
					newPrice={newPrice}
					handleOnClick={handleOnClick}
					lockable={lockable}
				/>
			</div>
			{lockable && (
				<div className="accept-special-price-footer">
					<div className="icon-text-item">
						<Icon shape={green_checkmark} />
						<span>
							{t.acceptedPrice} {formattedDate}
						</span>
					</div>
					<div className="icon-text-item">
						<Icon shape={green_checkmark} />
						<span>{t.specificallyTailored}</span>
					</div>
					<div className="icon-text-item">
						<Icon shape={green_checkmark} />
						<span>{t.afterSixMonths}</span>
					</div>
				</div>
			)}
		</div>
	);
};

const ResultText = ({ count, query, isLoading }: { count: number; query: string | null; isLoading: boolean }) => {
	const t = useTranslation().specialPrices;
	if (isLoading)
		return (
			<div className="search-result-text">
				{`${t.searchingFor} '${query}'...`}
				<div className="loading-spinner"></div>
			</div>
		);
	if (count > 0 || (count === 0 && query !== ''))
		return <div className="search-result-text">{`${t.resultsFor} '${query}' (${count} ${t.hits})`}</div>;
};

const PaginationFooter = ({
	count,
	page,
	loading,
	onClick,
}: {
	count: number;
	page: number;
	loading: boolean;
	onClick: () => void;
}) => {
	const t = useTranslation().specialPrices;
	return (
		<div className="search-footer">
			{count > page * LOOP54_DEFAULT_PAGE_SIZE && (
				<Button className="big" disabled={loading} onClick={onClick}>
					{loading ? t.loading : t.loadMore}
				</Button>
			)}
		</div>
	);
};
